import React, { useState, useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import getApiKey from '../../Apis/apiKey/getApiKey'
import QueryKeys from '../../utils/constants/queryKeys'
import regenerateApiKey from '../../Apis/apiKey/regenerateApiKey'
import LoaderDots from '../loaders/LoaderDots/LoaderDots'
import { Divider, makeStyles, Typography, Tooltip, Fade, Snackbar } from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import ButtonWithLoader from '../ui/ButtonWithLoader/ButtonWithLoader'
import UseQueryParams from '../../hooks/useQueryParams'
import ContainerPadding from '../containerPadding/containerPadding'
import apiKeyStyles from './apiKeyComponent.styles'
import MyCopyIcon from '../../assets/icons/MyCopyIcon'
import LogoIcon from '../../assets/icons/LogoIcon'

const ApiKeyComponent = () => {
  const queryParams = UseQueryParams()
  const { org: orgId } = queryParams
  const [showTooltip, setShowTooltip] = useState(false)
  const [openSnackbar, setOpenSnackbar] = useState(false)

  const useStyles = makeStyles(apiKeyStyles)
  const classes = useStyles()

  const queryClient = useQueryClient()

  // Derived from the query data rather than set in onSuccess, which doesn't
  // run when a remount is served from fresh cache.
  // Guarded on orgId: the org lands in the URL only once SetUrlAfterLogin has resolved it, so
  // a cold load of this page (the YouTube OAuth redirect comes back to a bare /apps/) renders
  // first with no org. Unguarded, that requests `v1/orgs/undefined/api-key` and the route's Guid
  // binding answers 400.
  const { data, isLoading } = useQuery([QueryKeys.GET_API_KEY, orgId], () => getApiKey(orgId), {
    enabled: Boolean(orgId),
  })
  const apiKey = data?.value ?? ''

  const regenerateApiKeyMutation = useMutation(() => regenerateApiKey(orgId), {
    onSuccess: (res) => {
      queryClient.setQueryData([QueryKeys.GET_API_KEY, orgId], res)
    },
  })

  useEffect(() => {
    let timeId = null
    if (showTooltip) {
      timeId = setTimeout(() => {
        setShowTooltip(false)
      }, 1500)
    }

    return () => {
      if (timeId) {
        clearTimeout(timeId)
      }
    }
  }, [showTooltip])

  const handleCopyIcon = (textToCopy) => {
    navigator.clipboard
      .writeText(textToCopy)
      .then(() => {
        setOpenSnackbar(true)
      })
      .catch(() => console.error('Failed to write to clipboard:'))
  }

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return
    }
    setOpenSnackbar(false)
  }

  return (
    <>
      <Divider variant='middle' />
      <ContainerPadding className={classes.paddingContainer}>
        <LogoIcon
          style={{ width: 300, height: 55, marginTop: 10, marginBottom: 10, marginLeft: -10 }}
        />
        <Typography variant='h6' className={classes.title}>
          API Key
        </Typography>
        <Typography>
          Integrate your application with our API for data loading and extraction.
        </Typography>
        {isLoading || !orgId ? (
          <LoaderDots />
        ) : (
          <div className={classes.apiContainer}>
            <div className={classes.apiKeyWrapper}>
              <Typography variant='body2'>
                {!!apiKey ? apiKey : 'No api key found, create a new one!'}
              </Typography>
              {!!apiKey && (
                <Tooltip
                  open={showTooltip}
                  title='Copied!'
                  placement='top'
                  TransitionComponent={Fade}
                  TransitionProps={{ timeout: 600 }}
                >
                  <div className={classes.copyIconWrapper}>
                    <MyCopyIcon
                      onClick={() => handleCopyIcon(apiKey)}
                      className={classes.copyIcon}
                    />
                  </div>
                </Tooltip>
              )}
            </div>
            <ButtonWithLoader
              loading={regenerateApiKeyMutation.isLoading}
              onClick={regenerateApiKeyMutation.mutate}
              label='Regenerate'
            />
          </div>
        )}
      </ContainerPadding>

      <Snackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        open={openSnackbar}
        autoHideDuration={2000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity='success' variant='filled'>
          API Key copied to clipboard
        </Alert>
      </Snackbar>
    </>
  )
}

export default ApiKeyComponent
